import {
  buildImageProxyUrl,
  buildStoreAdMediaProxyUrl,
  buildStorefrontMediaProxyUrl,
} from '../lib/image-proxy';
import { STORE_HOME_MERCHANDISING_SECTION_KEYS } from '../types/product';
import { resolveStorefrontPublicContentRefs } from './storefront-public-content-resolver.service';
import type { SupabaseClient } from '../supabase/client';
import type {
  StorefrontPageConfig,
  StorefrontPersistenceValidationSummary,
  StorefrontPublicCategoryPickerOption,
  StorefrontPublicCollectionPickerOption,
  StorefrontPublicContentPickerErrorCode,
  StorefrontPublicCustomProductCollectionPickerOption,
  StorefrontPublicContentPickerResult,
  StorefrontPublicMediaPickerOption,
  StorefrontPublicPromotionalAdPickerOption,
  StorefrontPublicProductPickerOption,
} from '../types/storefront-engine';
import {
  sanitizeStorefrontCustomProductCollectionDescription,
  sanitizeStorefrontCustomProductCollectionName,
  sanitizeStorefrontCustomProductCollectionRef,
} from '../lib/storefront-engine/custom-product-collections';

export interface ListStorefrontPublicProductPickerOptionsInput {
  readonly storeId: string;
  readonly search?: string | null;
  readonly categoryId?: string | null;
  readonly limit?: number;
}

export interface ListStorefrontPublicCategoryPickerOptionsInput {
  readonly storeId: string;
  readonly search?: string | null;
  readonly limit?: number;
}

export interface ListStorefrontPublicMediaPickerOptionsInput {
  readonly storeId: string;
  readonly search?: string | null;
  readonly role?: 'section-image';
  readonly limit?: number;
}

export interface ListStorefrontPublicPromotionalAdPickerOptionsInput {
  readonly storeId: string;
  readonly search?: string | null;
  readonly limit?: number;
}

export interface StorefrontCustomProductCollectionDetail {
  readonly products: readonly StorefrontPublicProductPickerOption[];
}

export interface ValidateStorefrontPublicContentRefsInput {
  readonly storeId: string;
  readonly config: StorefrontPageConfig;
  readonly sectionId?: string;
}

interface StorefrontPublicContentRpcClient {
  rpc(
    name: string,
    args: Record<string, unknown>,
  ): Promise<{ readonly data: unknown; readonly error: unknown }>;
}

interface ProductPickerRpcRow {
  readonly id: string;
  readonly name: string;
  readonly category_id: string;
  readonly category_name: string;
  readonly price_cents: number;
  readonly sale_price_cents: number | null;
  readonly currency: 'ILS';
  readonly image_path?: string | null;
  readonly image_url?: string | null;
  readonly available?: number | null;
  readonly is_out_of_stock?: boolean | null;
}

interface CategoryPickerRpcRow {
  readonly id: string;
  readonly name: string;
  readonly display_order: number;
  readonly product_count: number;
  readonly category_media_id?: string | null;
  readonly image_url?: string | null;
}

interface MediaPickerRpcRow {
  readonly id: string;
  readonly label: string;
  readonly role: 'section-image';
}

interface PromotionalAdPickerRpcRow {
  readonly id: string;
  readonly title: string | null;
  readonly alt_text: string | null;
  readonly display_order: number;
  readonly destinationSummaryKey?: string | null;
  readonly destination_summary_key?: string | null;
  readonly isSelectable?: boolean | null;
}

export interface CustomProductCollectionRpcRow {
  readonly collectionRef?: string | null;
  readonly collection_ref?: string | null;
  readonly name?: unknown;
  readonly description?: unknown;
  readonly productCount?: number | null;
  readonly product_count?: number | null;
  readonly isEmpty?: boolean | null;
  readonly is_empty?: boolean | null;
  readonly isSelectable?: boolean | null;
  readonly is_selectable?: boolean | null;
  readonly products?: unknown;
}

export async function invokeStorefrontPublicContentRpc(
  client: SupabaseClient,
  name: string,
  args: object,
): Promise<unknown> {
  const rpcClient = client as unknown as StorefrontPublicContentRpcClient;
  const { data, error } = await rpcClient.rpc(name, args as Record<string, unknown>);
  if (error) throw error;
  return data;
}

export function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

export function parseRows<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function toProductOption(
  row: ProductPickerRpcRow,
  supabaseUrl: string,
): StorefrontPublicProductPickerOption {
  const available = typeof row.available === 'number' ? row.available : null;
  const isOutOfStock = row.is_out_of_stock ?? available === 0;
  return {
    kind: 'product',
    id: row.id,
    name: row.name,
    categoryId: row.category_id,
    categoryName: row.category_name,
    price_cents: row.price_cents,
    sale_price_cents: row.sale_price_cents,
    currency: 'ILS',
    availabilityState: isOutOfStock ? 'out_of_stock' : 'in_stock',
    isOutOfStock,
    image_url:
      row.image_url ??
      (row.image_path ? buildImageProxyUrl(supabaseUrl, 'product-images', row.image_path) : null),
    isSelectable: true,
  };
}

function toCategoryOption(
  row: CategoryPickerRpcRow,
  supabaseUrl: string,
): StorefrontPublicCategoryPickerOption {
  return {
    kind: 'category',
    id: row.id,
    name: row.name,
    display_order: row.display_order,
    product_count: row.product_count,
    image_url:
      row.image_url ??
      (row.category_media_id
        ? buildImageProxyUrl(supabaseUrl, 'category-media', row.category_media_id)
        : null),
    isSelectable: true,
  };
}

function toMediaOption(
  row: MediaPickerRpcRow,
  supabaseUrl: string,
): StorefrontPublicMediaPickerOption {
  return {
    kind: 'media',
    id: row.id,
    label: row.label,
    role: row.role,
    image_url: buildStorefrontMediaProxyUrl(supabaseUrl, row.id),
    isSelectable: true,
  };
}

function normalizeDestinationSummaryKey(
  value: unknown,
): StorefrontPublicPromotionalAdPickerOption['destinationSummaryKey'] {
  const allowed = new Set<string>([
    'admin.promotionalAds.destination.summary.noLink',
    'admin.promotionalAds.destination.summary.sales',
    'admin.promotionalAds.destination.summary.category',
    'admin.promotionalAds.destination.summary.product',
    'admin.promotionalAds.destination.summary.external',
    'admin.promotionalAds.destination.summary.unavailable',
  ]);
  return typeof value === 'string' && allowed.has(value)
    ? (value as StorefrontPublicPromotionalAdPickerOption['destinationSummaryKey'])
    : 'admin.promotionalAds.destination.summary.unavailable';
}

function toPromotionalAdOption(
  row: PromotionalAdPickerRpcRow,
  supabaseUrl: string,
): StorefrontPublicPromotionalAdPickerOption {
  return {
    kind: 'promotional-ad',
    id: row.id,
    title: row.title,
    alt_text: row.alt_text,
    display_order: row.display_order,
    image_url: buildStoreAdMediaProxyUrl(supabaseUrl, row.id),
    destinationSummaryKey: normalizeDestinationSummaryKey(
      row.destinationSummaryKey ?? row.destination_summary_key,
    ),
    isSelectable: true,
  };
}

export function emptyStorefrontCustomProductCollectionText() {
  return { ar: '', he: '', en: '' };
}

export function toCustomProductCollectionOption(
  row: CustomProductCollectionRpcRow,
): StorefrontPublicCustomProductCollectionPickerOption | null {
  const collectionRef = sanitizeStorefrontCustomProductCollectionRef(
    row.collectionRef ?? row.collection_ref,
  );
  const name = sanitizeStorefrontCustomProductCollectionName(row.name);
  if (!collectionRef || !name) return null;
  const description =
    sanitizeStorefrontCustomProductCollectionDescription(row.description) ??
    emptyStorefrontCustomProductCollectionText();
  const productCount = Math.max(0, Math.trunc(row.productCount ?? row.product_count ?? 0));
  const isEmpty = row.isEmpty ?? row.is_empty ?? productCount === 0;
  const isSelectable = row.isSelectable ?? row.is_selectable ?? !isEmpty;
  return {
    kind: 'custom-product-collection',
    collectionRef,
    name,
    description,
    productCount,
    isEmpty,
    isSelectable,
  };
}

export function toCustomProductCollectionDetail(
  row: CustomProductCollectionRpcRow,
  supabaseUrl: string,
):
  | (StorefrontPublicCustomProductCollectionPickerOption & StorefrontCustomProductCollectionDetail)
  | null {
  const option = toCustomProductCollectionOption(row);
  if (!option) return null;
  return {
    ...option,
    products: parseRows<ProductPickerRpcRow>(row.products).map((product) =>
      toProductOption(product, supabaseUrl),
    ),
  };
}

export async function listStorefrontPublicProductPickerOptions(
  client: SupabaseClient,
  input: ListStorefrontPublicProductPickerOptionsInput,
  options: { readonly supabaseUrl: string },
): Promise<StorefrontPublicProductPickerOption[]> {
  const data = await invokeStorefrontPublicContentRpc(
    client,
    'list_storefront_public_product_picker_options',
    {
      target_store_id: input.storeId,
      search_query: input.search?.trim() || null,
      target_category_id: input.categoryId ?? null,
      page_limit: input.limit ?? 20,
    },
  );
  return parseRows<ProductPickerRpcRow>(data).map((row) =>
    toProductOption(row, options.supabaseUrl),
  );
}

export async function listStorefrontPublicCategoryPickerOptions(
  client: SupabaseClient,
  input: ListStorefrontPublicCategoryPickerOptionsInput,
  options: { readonly supabaseUrl: string },
): Promise<StorefrontPublicCategoryPickerOption[]> {
  const data = await invokeStorefrontPublicContentRpc(
    client,
    'list_storefront_public_category_picker_options',
    {
      target_store_id: input.storeId,
      search_query: input.search?.trim() || null,
      page_limit: input.limit ?? 20,
    },
  );
  return parseRows<CategoryPickerRpcRow>(data).map((row) =>
    toCategoryOption(row, options.supabaseUrl),
  );
}

export async function listStorefrontPublicMediaPickerOptions(
  client: SupabaseClient,
  input: ListStorefrontPublicMediaPickerOptionsInput,
  options: { readonly supabaseUrl: string },
): Promise<StorefrontPublicMediaPickerOption[]> {
  const data = await invokeStorefrontPublicContentRpc(
    client,
    'list_storefront_public_media_picker_options',
    {
      target_store_id: input.storeId,
      search_query: input.search?.trim() || null,
      target_role: input.role ?? 'section-image',
      page_limit: input.limit ?? 20,
    },
  );
  return parseRows<MediaPickerRpcRow>(data).map((row) => toMediaOption(row, options.supabaseUrl));
}

export async function listStorefrontPublicPromotionalAdPickerOptions(
  client: SupabaseClient,
  input: ListStorefrontPublicPromotionalAdPickerOptionsInput,
  options: { readonly supabaseUrl: string },
): Promise<StorefrontPublicPromotionalAdPickerOption[]> {
  const data = await invokeStorefrontPublicContentRpc(
    client,
    'list_storefront_public_promotional_ad_picker_options',
    {
      target_store_id: input.storeId,
      search_query: input.search?.trim() || null,
      page_limit: input.limit ?? 20,
    },
  );
  return parseRows<PromotionalAdPickerRpcRow>(data).map((row) =>
    toPromotionalAdOption(row, options.supabaseUrl),
  );
}

export function listStorefrontPublicCollectionPickerOptions(): readonly StorefrontPublicCollectionPickerOption[] {
  const labels = {
    best_sellers: {
      labelKey: 'admin.storefrontEngine.sections.productRail.bestSellers.name',
      descriptionKey: 'admin.storefrontEngine.sections.productRail.bestSellers.description',
    },
    trending_now: {
      labelKey: 'admin.storefrontEngine.sections.productRail.trendingNow.name',
      descriptionKey: 'admin.storefrontEngine.sections.productRail.trendingNow.description',
    },
    new_arrivals: {
      labelKey: 'admin.storefrontEngine.sections.productRail.newArrivals.name',
      descriptionKey: 'admin.storefrontEngine.sections.productRail.newArrivals.description',
    },
    special_offers: {
      labelKey: 'admin.storefrontEngine.sections.productRail.specialOffers.name',
      descriptionKey: 'admin.storefrontEngine.sections.productRail.specialOffers.description',
    },
  } satisfies Record<
    (typeof STORE_HOME_MERCHANDISING_SECTION_KEYS)[number],
    Pick<StorefrontPublicCollectionPickerOption, 'labelKey' | 'descriptionKey'>
  >;
  return STORE_HOME_MERCHANDISING_SECTION_KEYS.map((key) => ({
    kind: 'collection',
    key,
    ...labels[key],
    isSelectable: true,
  }));
}

export async function validateStorefrontPublicContentRefs(
  client: SupabaseClient,
  input: ValidateStorefrontPublicContentRefsInput,
): Promise<StorefrontPublicContentPickerResult<StorefrontPersistenceValidationSummary>> {
  const data = asRecord(
    await invokeStorefrontPublicContentRpc(
      client,
      'validate_storefront_public_content_refs_for_admin',
      {
        target_store_id: input.storeId,
        page_config: input.config,
        target_section_id: input.sectionId ?? null,
      },
    ),
  );
  const ok = data.ok === true;
  const summary: StorefrontPersistenceValidationSummary = {
    ok,
    issueCount: typeof data.issueCount === 'number' ? data.issueCount : 0,
    errorCodes: Array.isArray(data.errorCodes) ? (data.errorCodes as never[]) : [],
    issues: Array.isArray(data.issues) ? (data.issues as never[]) : [],
  };
  return ok
    ? { ok: true, data: summary }
    : {
        ok: false,
        code: 'invalid_reference',
        messageKey: 'admin.storefrontEngine.settings.errors.invalidReference',
        issues: Array.isArray(data.issues) ? (data.issues as never[]) : [],
      };
}

export function mapStorefrontPublicContentPickerError(
  error: unknown,
): StorefrontPublicContentPickerErrorCode {
  if (error && typeof error === 'object') {
    const maybe = error as {
      readonly code?: string;
      readonly message?: string;
      readonly name?: string;
    };
    if (maybe.code === '42501') return 'permission_denied';
    if (/network|fetch|abort|load failed/i.test(`${maybe.name ?? ''} ${maybe.message ?? ''}`)) {
      return 'network_failed';
    }
  }
  return 'unknown';
}

export const storefrontPublicContentPickerService = {
  listProducts: listStorefrontPublicProductPickerOptions,
  listCategories: listStorefrontPublicCategoryPickerOptions,
  listMedia: listStorefrontPublicMediaPickerOptions,
  listPromotionalAds: listStorefrontPublicPromotionalAdPickerOptions,
  listCollections: listStorefrontPublicCollectionPickerOptions,
  validateCandidateConfig: validateStorefrontPublicContentRefs,
  resolvePublicRefs: resolveStorefrontPublicContentRefs,
  mapError: mapStorefrontPublicContentPickerError,
};
